"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useViewpointMarkups = useViewpointMarkups;
var _react = require("react");
var _ViewpointContext = require("./ViewpointContext");
var _ViewpointExtensionType = require("./ViewpointExtensionType");
// This is provided by the GG markups extension.
const VIEWPOINT_EXTENSION_NAME = "Autodesk.GG.Markups";

/**
 * Provides access to the viewpoint extension in order to start/stop viewpoint mode and take screenshots.
 */
function useViewpointMarkups(viewerInstance) {
  const viewerFromContext = (0, _react.useContext)(_ViewpointContext.ViewerContext);

  // We should have a single instance of Viewer in the app, so we should never have both of these defined.
  if (!!viewerFromContext && !!viewerInstance) {
    throw new Error("Viewer instance must be provided through context or as a parameter, but not both.");
  }
  const [isViewpointModeEnabled, setViewpointModeEnabled] = (0, _react.useState)(false);
  const viewpointExtension = useExtension(viewerFromContext !== null && viewerFromContext !== void 0 ? viewerFromContext : viewerInstance, VIEWPOINT_EXTENSION_NAME);
  useEventListener(viewpointExtension, _ViewpointExtensionType.ViewpointEventType.VIEWPOINT_MODE_ENABLED_CHANGE, () => {
    var _viewpointExtension$i;
    setViewpointModeEnabled((_viewpointExtension$i = viewpointExtension === null || viewpointExtension === void 0 ? void 0 : viewpointExtension.isViewpointModeEnabled) !== null && _viewpointExtension$i !== void 0 ? _viewpointExtension$i : false);
  });

  // Binding the extension to it's methods so it can be used like this:
  // ```
  // const { isViewpointModeEnabled, startViewpointMode, stopViewpointMode, takeScreenshot } = useViewpointMarkups();
  // ```
  //
  // Also adding null checks so this can be used without the extension loaded (i.e. in unit tests)
  const boundExtension = (0, _react.useMemo)(() => ({
    startViewpointMode: issueId => {
      var _viewpointExtension$s;
      return (_viewpointExtension$s = viewpointExtension === null || viewpointExtension === void 0 ? void 0 : viewpointExtension.startViewpointMode(issueId)) !== null && _viewpointExtension$s !== void 0 ? _viewpointExtension$s : Promise.resolve({
        type: "DISCARD"
      });
    },
    stopViewpointMode: (result, force) => {
      var _viewpointExtension$s2;
      return (_viewpointExtension$s2 = viewpointExtension === null || viewpointExtension === void 0 ? void 0 : viewpointExtension.stopViewpointMode(result, force)) !== null && _viewpointExtension$s2 !== void 0 ? _viewpointExtension$s2 : Promise.resolve(false);
    },
    takeScreenshot: issueId => {
      var _viewpointExtension$t;
      return (_viewpointExtension$t = viewpointExtension === null || viewpointExtension === void 0 ? void 0 : viewpointExtension.takeScreenshot(issueId)) !== null && _viewpointExtension$t !== void 0 ? _viewpointExtension$t : Promise.reject();
    }
  }), [viewpointExtension]);
  return {
    isViewpointModeEnabled,
    ...boundExtension
  };
}
function useEventListener(eventDispatcher, eventName, callback) {
  (0, _react.useEffect)(() => {
    if (!eventName || !eventDispatcher) {
      return;
    }
    if (!eventDispatcher.addEventListener || !eventDispatcher.addEventListener) {
      console.error('[useEventListener] dispatcher is missing "addEventListener" or "removeEventListener"');
      return;
    }
    const name = eventName;
    const dispatcher = eventDispatcher;
    const activeState = {
      isActive: true
    };
    const cb = function () {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      return callback(...args, activeState);
    };
    dispatcher.addEventListener(name, cb, {
      // This very low priority is set to make sure all viewer events are processed first,
      // before the UI.
      priority: -10001
    });
    return () => {
      activeState.isActive = false;
      dispatcher.removeEventListener(name, cb);
    };
  }, [eventDispatcher, eventName, callback]);
}
function useExtension(viewer, extensionName) {
  var _window$Autodesk, _window$Autodesk2;
  const ref = (0, _react.useRef)(getExtension(viewer, extensionName));
  const onExtensionUpdate = (0, _react.useCallback)(_ref => {
    let {
      extensionId
    } = _ref;
    if (extensionName === extensionId) {
      ref.current = getExtension(viewer, extensionName);
    }
  }, [viewer, extensionName]);
  useEventListener(viewer, (_window$Autodesk = window.Autodesk) === null || _window$Autodesk === void 0 || (_window$Autodesk = _window$Autodesk.Viewing) === null || _window$Autodesk === void 0 ? void 0 : _window$Autodesk.EXTENSION_LOADED_EVENT, onExtensionUpdate);
  useEventListener(viewer, (_window$Autodesk2 = window.Autodesk) === null || _window$Autodesk2 === void 0 || (_window$Autodesk2 = _window$Autodesk2.Viewing) === null || _window$Autodesk2 === void 0 ? void 0 : _window$Autodesk2.EXTENSION_UNLOADED_EVENT, onExtensionUpdate);
  return ref.current;
}
function getExtension(viewer, extensionName) {
  return (viewer === null || viewer === void 0 ? void 0 : viewer.getExtension(extensionName)) || null;
}
//# sourceMappingURL=useViewpointMarkups.js.map